<template>
	<div class="admin-author-profile">
		<ChangePriceForAuthor
			:active.sync="pricePopupState"
			@confirm="changeAuthorPrice"
		/>

		<ConfirmActionPopup
			:active.sync="problemAuthorPopupState"
			@confirm="markAuthorAsProblem"
		/>

		<PenaltyPopup
			:active.sync="penaltyPopupState"
			:maxValue="authorIncome"
			@confirm="setAuthorPenalty"
		/>

		<AuthorsWidgets
			:penalty-count="penaltyCount"
			:active-orders-count="activeOrdersNumber"
			:author-income="authorIncome"
			:count-task-active="countTaskActive"
			:count-task="countTask"
		/>

		<div v-if="bankCards.length && !hasMainBankCard" class="vx-row">
			<div class="vx-col mb-5">
				<vx-card class="bg-danger">
					<span class="text-white" :style="{ fontWeight: 'bold' }">
						{{ $t("authorDidNotChooseMainBankCard") }}
					</span>
				</vx-card>
			</div>
		</div>

		<div class="vx-row">
			<div class="vx-col w-full mb-5">
				<vx-card>
					<div class="d-flex align-center">
						<div class="vx-col">
							<h1 class="m-0 mr-2">{{ $t("profile") }}</h1>
						</div>

						<div
							v-if="model.is_solar_staff"
							class="vx-col bg-warning px-2 rounded text-white"
						>
							<p>solar-staff</p>
						</div>
					</div>

					<div class="info mt-5 flex flex-col sm:flex-row">
						<div class="photo">
							<img :src="model.photo" alt="" />
						</div>

						<div class="data">
							<div class="tables vx-row">
								<table class="vx-col w-full lg:w-1/2">
									<tbody class="data__left-side">
										<tr>
											<td>{{ $t("personName") }}</td>
											<td>
												{{ model.first_name }}
												{{ model.last_name }}
											</td>
										</tr>

										<tr>
											<td>{{ $t("nickname") }}</td>
											<td>{{ model.username }}</td>
										</tr>

										<tr>
											<td>E-Mail</td>
											<td>{{ model.email }}</td>
										</tr>

										<tr>
											<td>{{ $t("emailForPayment") }}</td>
											<td>{{ model.email_payment }}</td>
										</tr>

										<tr v-if="bankCards.length">
											<td>{{ $t("bankCards") }}</td>
											<td>
												<div class="cards-list">
													<div
														v-for="item in bankCards"
														:key="item.id"
														:class="{
															selected:
																item.selected
														}"
														class="cards-list__item"
													>
														{{ item.number }}
													</div>
												</div>
											</td>
										</tr>

										<tr>
											<td>{{ $t("phone") }}</td>
											<td>{{ model.phone }}</td>
										</tr>

										<tr>
											<td>{{ $t("qualityLevel") }}</td>
											<td>
												{{
													$getDeep(
														model.quality_level,
														levels,
														"level"
													)
												}}
											</td>
										</tr>
									</tbody>
								</table>

								<table
									class="vx-col w-full lg:w-1/2 mt-10 lg:mt-0"
								>
									<tbody>
										<tr>
											<td>{{ $t("birthDate") }}</td>
											<td>{{ birthDateOutput }}</td>
										</tr>

										<tr>
											<td>{{ $t("sex") }}</td>
											<td>
												{{
													$getDeep(
														model.gender,
														genders
													)
												}}
											</td>
										</tr>

										<tr>
											<td>{{ $t("country") }}</td>
											<td>
												{{
													$getDeep(
														model.country,
														countries
													)
												}}
											</td>
										</tr>

										<tr>
											<td>{{ $t("language") }}</td>
											<td>
												<div class="d-flex flex-wrap">
													{{ languagesOutput }}
												</div>
											</td>
										</tr>

										<tr>
											<td>{{ $t("subjects") }}</td>
											<td>
												<div class="d-flex flex-wrap">
													{{ subjectsOutput }}
												</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div class="about">
								<b>{{ $t("aboutYourself") }}</b>
								<p>{{ model.about }}</p>
							</div>
						</div>
					</div>

					<ProfileActions
						:disabled="loading"
						@edit-action="$router.push($route.path + '/edit')"
						@change-balance="pricePopupState = true"
						@set-problem-state="problemAuthorPopupState = true"
						@set-penalty-state="penaltyPopupState = true"
					/>
				</vx-card>
			</div>
		</div>

		<div v-if="penaltyTable.length" class="vx-row">
			<div class="vx-col w-full mb-5">
				<vx-card>
					<h2 class="mb-4">
						{{ $t("stats.penalties") }}
					</h2>

					<vs-table
						max-items="10"
						pagination
						:data="penaltyTable"
						class="table-th-center"
					>
						<template slot="thead">
							<vs-th
								v-for="fil in penaltyTableFields"
								class="justify-center table-title"
								:class="{
									'justify-center':
										fil.align && fil.align === 'center',
									'text-center':
										fil.align && fil.align === 'center'
								}"
								:sort-key="fil.field.split('.')[0]"
							>
								{{ fil.title }}
							</vs-th>
						</template>

						<template slot-scope="{ data }">
							<vs-tr :key="indextr" v-for="(tr, indextr) in data">
								<template v-for="fil in penaltyTableFields">
									<vs-td
										v-if="fil.field === 'date'"
										:class="{
											'text-center':
												fil.align &&
												fil.align === 'center'
										}"
									>
										{{ tr[fil.field] }}
									</vs-td>

									<vs-td
										:class="{
											'text-center':
												fil.align &&
												fil.align === 'center'
										}"
										v-else-if="fil.field === 'order_id'"
									>
										{{ tr[fil.field] }}
									</vs-td>

									<vs-td
										:class="{
											'text-center':
												fil.align &&
												fil.align === 'center'
										}"
										v-else-if="fil.field === 'amount'"
									>
										{{
											+tr[fil.field] === 0
												? $t("warning")
												: tr[fil.field]
										}}
									</vs-td>

									<vs-td
										:class="{
											'text-center':
												fil.align &&
												fil.align === 'center'
										}"
										v-else-if="
											fil.field === 'penalty_reason'
										"
									>
										{{ tr[fil.field] }}
									</vs-td>

									<vs-td
										:class="{
											'text-center':
												fil.align &&
												fil.align === 'center'
										}"
										v-else
									>
										...
									</vs-td>
								</template>
							</vs-tr>
						</template>
					</vs-table>
				</vx-card>
			</div>
		</div>

		<AuthorsCommentsList
			v-if="userRole !== 'author'"
			:author-id="authorId"
			class="mt-3"
		/>

		<AuthorsActiveOrdersTable
			v-if="userRole === 'admin'"
			:data="activeOrders"
		/>
	</div>
</template>

<script>
import databaseManager from "@/mixins/databaseManager";
import notifier from "@/mixins/notifier";
import ChangePriceForAuthor from "@/components/dialogs/ChangePriceForAuthor.vue";
import ConfirmActionPopup from "@/components/dialogs/ConfirmActionPopup.vue";
import PenaltyPopup from "@/components/dialogs/PenaltyPopup.vue";
import AuthorsWidgets from "@/components/profile/author/AuthorsWidgets.vue";
import ProfileActions from "@/components/profile/author/ProfileActions.vue";
import AuthorsActiveOrdersTable from "@/components/profile/author/AuthorsActiveOrdersTable.vue";
import AuthorsCommentsList from '@/components/authors/authors-comments-list/AuthorsCommentsList.vue'

export default {
	name: "Author",

	components: {
		AuthorsCommentsList,
		ChangePriceForAuthor,
		ConfirmActionPopup,
		PenaltyPopup,
		AuthorsWidgets,
		ProfileActions,
		AuthorsActiveOrdersTable
	},
	mixins: [databaseManager, notifier],
	data() {
		return {
			popupActive: false,
			val: "",
			description: "",
			countries: [],
			genders: [],
			languages: [],
			subjects: [],
			levels: [],
			countTask: {},
			countTaskActive: {},
			model: {},
			pricePopupState: false,
			authorIncome: 0,
			loading: false,
			problemAuthorPopupState: false,
			penaltyPopupState: false,
			penaltyData: null,
			penaltyTableFields: [
				{
					field: "date",
					title: this.$t("date")
				},
				{
					field: "order_id",
					title: this.$t("order"),
					align: "center"
				},
				{
					field: "amount",
					title: this.$t("penaltyAmount"),
					align: "center"
				},
				{
					field: "penalty_reason",
					title: this.$t("penaltyReason")
				}
			],
			activeOrdersNumber: 0,
			activeOrders: []
		};
	},
	computed: {
		authorId() {
			return this.$route.params.id || null;
		},
		userRole() {
			return this.$store.state.auth.role;
		},
		locale() {
			return this.$store.state.locale;
		},
		languagesOutput() {
			if (
				!this.model === undefined ||
				!this.model.languages ||
				!this.model.languages.length
			)
				return;

			return this.model.languages
				.map(el => el[`name_${this.locale}`])
				.join(", ");
		},
		subjectsOutput() {
			if (!this.model === undefined || !this.model.subjects) return null;

			let output = [];
			for (let subject of this.model.subjects) {
				output.push(this.$getDeep(subject, this.subjects));
			}
			return output.join(", ");
		},
		birthDateOutput() {
			if (!this.model === undefined || !this.model.birth_date)
				return null;

			return this.model.birth_date
				.split("-")
				.reverse()
				.join(".");
		},
		penaltyTable() {
			return this.penaltyData ? this.penaltyData.data : [];
		},
		penaltyCount() {
			return this.penaltyData ? this.penaltyData.count : 0;
		},

		hasMainBankCard() {
			const mainCard = this.bankCards.find(el => el.selected);
			return !!mainCard;
		},

		bankCards() {
			const cards =
				this.model && this.model.bank_cards
					? this.model.bank_cards
					: [];
			return cards;
		}
	},

	async mounted() {
		try {
			const role = this.$store.state.auth.role;
			const requests = [
				async () => {
					this.languages = (
						await this.$http.get("/settings/get-languages")
					).data.data;
				},
				async () => {
					this.subjects = (
						await this.$http.get("/settings/get-subjects")
					).data.data;
				},
				async () => {
					this.countries = (
						await this.$http.get("/settings/get-countries")
					).data.data;
				},
				async () => {
					this.genders = (
						await this.$http.get("/settings/get-genders")
					).data.data;
				},
				async () => {
					this.levels = (
						await this.$http.get(
							`/${role}/author/get-quality-level-list`
						)
					).data.data;
				},
				async () => {
					this.countTask = (
						await this.$http.get(
							`/${role}/author/get-orders-completed-count/${this.$route.params.id}`
						)
					).data.data;
				},
				async () => {
					this.countTaskActive = (
						await this.$http.get(
							`/${role}/author/get-success-rate/${this.$route.params.id}`
						)
					).data.data;
				},
				async () => {
					this.penaltyData = await this.loadPenaltyData(
						this.authorId
					);
				}
			];
			await Promise.all(requests.map(item => item()));

			if (role === "admin") {
				const adminRequests = [
					async () => {
						this.model = (
							await this.$http(
								`/${role}/author/view/${this.$route.params.id}`
							)
						).data.data;
					},
					async () => {
						const res = await this.dbGetAuthorBalance(
							this.authorId
						);
						this.authorIncome = res.amount;
					},
					async () => {
						const res = await this.dbGetActiveOrdersNumber(
							this.authorId
						);
						this.activeOrdersNumber = res.number;
					},
					async () => {
						const res = await this.dbGetActiveOrdersList(
							this.authorId
						);
						this.activeOrders = res.map(el => {
							return {
								...el,
								amount: +el.amount
							};
						});
					}
				];

				await Promise.all(adminRequests.map(item => item()));
			} else if (role === "manager") {
				this.model = (
					await this.$http(
						`/${role}/author/profile/${this.$route.params.id}`
					)
				).data.data;
			}
		} catch (e) {
			console.log(e);
		}
	},

	methods: {
		async setAuthorPenalty({ orderId, reason, amount }) {
			try {
				const res = await this.dbSetAuthorPenalty(
					this.authorId,
					orderId,
					reason,
					amount
				);
				if (!res) return;
				this.authorIncome = res.amount;
				this.notifySuccess();
				this.penaltyData = await this.loadPenaltyData(this.authorId);
			} catch (e) {
				this.notifyError(e.message);
			}
		},
		async changeAuthorPrice(action, amount) {
			this.loading = true;
			try {
				const res = await this.dbChangeAuthorBalance(
					this.authorId,
					amount,
					action
				);
				this.authorIncome = res.amount;
				this.notifySuccess(this.$t("changedSuccessfully"));
			} catch (e) {
				this.notifyError(e.message);
			} finally {
				this.loading = false;
			}
		},
		async markAuthorAsProblem() {
			this.loading = true;
			let url;
			let successMessage;
			if (this.model.is_problem) {
				url = `/admin/author/unmark-problem/${this.authorId}`;
				successMessage = "";
			} else {
				url = `/admin/author/mark-problem/${this.authorId}`;
				successMessage = "authorMarkedAsProblem";
			}
			try {
				const res = (await this.$http.post(url)).data;
				if (!res) return;
				this.notifySuccess(this.$t(successMessage));
				this.model = (
					await this.$http(`/admin/author/view/${this.authorId}`)
				).data.data;
			} catch (e) {
				this.notifyError(e.message);
			} finally {
				this.loading = false;
			}
		},
		async loadPenaltyData(id) {
			try {
				return await this.dbGetAuthorPenaltyList(id);
			} catch (e) {
				this.notifyError(e.message);
			}
		}
	}
};
</script>

<style lang="scss">
@import "../../assets/scss/vuexy/variables";
@import "../../assets/scss/vuexy/functions";

.admin-author-profile {
	.data {
		width: 100%;

		.cards-list {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;
			align-items: center;
			grid-gap: 10px;

			&__item {
				white-space: nowrap;

				&.selected {
					border-radius: 4px;
					padding: 2px 4px;
					margin: 0 -2px;
					background: $success;
					color: #fff;
				}
			}
		}
	}

	h1 {
		font-size: 18px;
		color: #626262;
		margin-bottom: 15px;
		font-weight: 700;
	}

	.vx-card {
		height: 100%;
	}

	.info {
		display: flex;

		.tables {
			display: flex;
		}

		table {
			max-width: 550px;
			margin-bottom: 20px;
			padding-left: 15px;

			.data__left-side {
				tr {
					td {
						width: 60%;

						&:first-child {
							width: 40%;
							padding-right: 10px;
						}
					}
				}
			}

			tr {
				td {
					padding-bottom: 7px;
					width: 50%;
					vertical-align: top;

					&:first-child {
						max-width: 250px;
						font-weight: 600;
					}
				}
			}
		}

		.about {
			display: flex;

			b {
				flex-shrink: 0;
				max-width: 258px;
				width: 25%;
			}

			@media screen and (max-width: 992px) {
				b {
					width: 50%;
				}
			}
		}
	}

	.photo {
		width: 200px;
		height: 200px;
		border-radius: 10px;
		overflow: hidden;
		margin-right: 30px;
		margin-bottom: 15px;
		flex-shrink: 0;

		img {
			max-width: 100%;
			width: 100%;
			height: 100%;
		}
	}

	.box {
		display: flex;
		font-size: 25px;
		align-items: center;
		margin-bottom: 22px;
	}

	.bottom {
		display: flex;
		justify-content: flex-end;
	}

	.table-title {
		&.text-center {
			.vs-table-text {
				justify-content: center;
			}
		}
	}
}
</style>
